<template>
  <section class="invoice-create-page">
    <div class="invoice-create-top-grid payment-receipt-detail-top-grid">
      <article class="invoice-create-card invoice-create-card--accent-billing payment-receipt-detail-card">
        <header class="invoice-create-card__head">
          <h3>{{ text.detail.cards.operation.title }}</h3>
        </header>

        <div class="payment-receipt-detail-operation-split">
          <section class="payment-receipt-detail-operation-pane">
            <dl class="payment-receipt-detail-list">
              <div class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.operation.fields.status }}</dt>
                <dd>
                  <span class="app-status-pill" :class="statusClass">{{ statusLabel }}</span>
                </dd>
              </div>

              <div class="payment-receipt-detail-row payment-receipt-detail-row--supplier">
                <dt>{{ text.detail.cards.operation.fields.supplier }}</dt>
                <dd>
                  <router-link
                    v-if="supplierRoute"
                    :to="supplierRoute"
                    class="payment-receipt-detail-link"
                  >
                    {{ supplierName }}
                  </router-link>
                  <span v-else>{{ supplierName }}</span>
                </dd>
              </div>

              <div class="payment-receipt-detail-row payment-receipt-detail-row--multiline">
                <dt>{{ text.detail.cards.operation.fields.business }}</dt>
                <dd>
                  <template v-if="businessLines.length">
                    <div
                      v-for="line in businessLines"
                      :key="line.key"
                      class="payment-receipt-detail-multiline-item"
                      :title="line.label"
                    >
                      <router-link
                        v-if="line.businessRoute"
                        :to="line.businessRoute"
                        class="payment-receipt-detail-link"
                      >
                        {{ line.label }}
                      </router-link>
                      <span v-else>{{ line.label }}</span>
                    </div>
                  </template>
                  <span v-else>{{ fallback }}</span>
                </dd>
              </div>

              <div class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.operation.fields.sent }}</dt>
                <dd>{{ sentLabel }}</dd>
              </div>

              <div class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.operation.fields.lastSent }}</dt>
                <dd>{{ lastSentLabel }}</dd>
              </div>
            </dl>
          </section>

          <section class="payment-receipt-detail-operation-pane payment-receipt-detail-operation-pane--totals">
            <dl class="payment-receipt-detail-list">
              <div class="payment-receipt-detail-row payment-receipt-detail-row--total">
                <dt>{{ text.detail.cards.operation.fields.total }}</dt>
                <dd>{{ totalLabel }}</dd>
              </div>
            </dl>
          </section>
        </div>
      </article>

      <article class="invoice-create-card invoice-create-card--accent-info payment-receipt-detail-card">
        <header class="invoice-create-card__head">
          <h3>{{ summaryTitle }}</h3>

          <button
            v-if="canToggleSummary"
            type="button"
            class="payment-receipt-detail-summary-toggle"
            @click="showFullSummary = !showFullSummary"
          >
            <i class="fa" :class="showFullSummary ? 'fa-angle-up' : 'fa-angle-down'" aria-hidden="true"></i>
            {{ showFullSummary ? summaryToggleLessLabel : summaryToggleMoreLabel }}
          </button>
        </header>

        <dl class="payment-receipt-detail-list">
          <div
            v-if="showReceiverRfc || showReceiverBusinessName"
            class="payment-receipt-detail-row payment-receipt-detail-row--dual payment-receipt-detail-row--separator"
          >
            <div>
              <dt>{{ text.detail.cards.fiscalSummary.fields.receiverRfc }}</dt>
              <dd>{{ showReceiverRfc ? receiverRfc : '' }}</dd>
            </div>

            <div>
              <dt>{{ text.detail.cards.fiscalSummary.fields.businessName }}</dt>
              <dd>{{ showReceiverBusinessName ? receiverBusinessName : '' }}</dd>
            </div>
          </div>

          <div
            v-if="showFolio || showCfdiFolio"
            class="payment-receipt-detail-row payment-receipt-detail-row--dual"
          >
            <div v-if="showFolio">
              <dt>{{ text.detail.cards.fiscalSummary.fields.folio }}</dt>
              <dd>{{ folio }}</dd>
            </div>

            <div v-if="showCfdiFolio">
              <dt>{{ text.detail.cards.fiscalSummary.fields.cfdiFolio }}</dt>
              <dd>{{ cfdiFolio }}</dd>
            </div>
          </div>

          <div v-if="showEmissionDate" class="payment-receipt-detail-row">
            <dt>{{ text.detail.cards.fiscalSummary.fields.issueDate }}</dt>
            <dd>{{ emissionDateLabel }}</dd>
          </div>

          <div v-if="showCfdiUuid" class="payment-receipt-detail-row">
            <dt>{{ text.detail.cards.fiscalSummary.fields.uuid }}</dt>
            <dd>{{ cfdiUuid }}</dd>
          </div>

          <div v-if="showCurrencyExchange" class="payment-receipt-detail-row">
            <dt>{{ text.detail.cards.fiscalSummary.fields.currencyExchange }}</dt>
            <dd>{{ currencyExchange }}</dd>
          </div>
        </dl>

        <section class="payment-receipt-detail-summary-payment-block">
          <div class="payment-receipt-detail-summary-split">
            <section class="payment-receipt-detail-summary-pane">
              <dl class="payment-receipt-detail-list">
                <div class="payment-receipt-detail-row">
                  <dt>{{ text.detail.cards.operation.fields.paymentAccount }}</dt>
                  <dd>{{ paymentAccount }}</dd>
                </div>

                <div class="payment-receipt-detail-row">
                  <dt>{{ text.detail.cards.operation.fields.paymentForm }}</dt>
                  <dd>{{ paymentForm }}</dd>
                </div>
              </dl>
            </section>

            <section class="payment-receipt-detail-summary-pane payment-receipt-detail-summary-pane--operation">
              <dl class="payment-receipt-detail-list">
                <div class="payment-receipt-detail-row">
                  <dt>{{ text.detail.cards.operation.fields.operationNumber }}</dt>
                  <dd>{{ operationNumber }}</dd>
                </div>
              </dl>
            </section>
          </div>
        </section>

        <div v-if="canToggleSummary && showFullSummary" class="payment-receipt-detail-summary-expanded">
          <section class="payment-receipt-detail-summary-block">
            <h4>{{ text.detail.cards.fiscalSummary.sections.emitter }}</h4>
            <dl class="payment-receipt-detail-list">
              <div v-if="issuerRfc !== ''" class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.fiscalSummary.fields.rfc }}</dt>
                <dd>{{ issuerRfc }}</dd>
              </div>

              <div v-if="issuerBusinessName !== ''" class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.fiscalSummary.fields.businessName }}</dt>
                <dd>{{ issuerBusinessName }}</dd>
              </div>

              <div v-if="issuerTaxRegime !== ''" class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.fiscalSummary.fields.taxRegime }}</dt>
                <dd>{{ issuerTaxRegime }}</dd>
              </div>

              <div v-if="issuerFiscalResidence !== ''" class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.fiscalSummary.fields.fiscalResidence }}</dt>
                <dd>{{ issuerFiscalResidence }}</dd>
              </div>
            </dl>
          </section>

          <section v-if="showBankDetailsOnExpanded" class="payment-receipt-detail-summary-block">
            <h4>{{ text.detail.cards.fiscalSummary.sections.bankData }}</h4>
            <dl class="payment-receipt-detail-list">
              <div class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.fiscalSummary.fields.beneficiaryBankRfc }}</dt>
                <dd>{{ beneficiaryBankRfc }}</dd>
              </div>

              <div class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.fiscalSummary.fields.beneficiaryAccount }}</dt>
                <dd>{{ beneficiaryAccount }}</dd>
              </div>

              <div class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.fiscalSummary.fields.orderingBank }}</dt>
                <dd>{{ orderingBankName }}</dd>
              </div>

              <div class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.fiscalSummary.fields.orderingBankRfc }}</dt>
                <dd>{{ orderingBankRfc }}</dd>
              </div>

              <div class="payment-receipt-detail-row">
                <dt>{{ text.detail.cards.fiscalSummary.fields.orderingBankAccount }}</dt>
                <dd>{{ orderingBankAccount }}</dd>
              </div>
            </dl>
          </section>
        </div>
      </article>
    </div>

    <article class="invoice-create-card invoice-create-concepts-card">
      <header class="invoice-create-card__head">
        <h3>{{ text.detail.cards.concepts.title }}</h3>
      </header>

      <div class="app-table-wrap invoice-create-concepts-table-wrap">
        <div class="app-table-scroll">
          <table class="app-data-table">
            <thead>
              <tr>
                <th>{{ text.detail.cards.concepts.table.headers.date || 'Fecha' }}</th>
                <th>{{ text.detail.cards.concepts.table.headers.folio || 'Folio' }}</th>
                <th>{{ text.detail.cards.concepts.table.headers.category || 'Categoria' }}</th>
                <th>{{ text.detail.cards.concepts.table.headers.business }}</th>
                <th>{{ text.detail.cards.concepts.table.headers.concept }}</th>
                <th class="app-text-right">{{ text.detail.cards.concepts.table.headers.amount }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="paymentConceptRows.length === 0">
                <td colspan="6" class="app-table-empty">{{ text.detail.cards.concepts.table.empty }}</td>
              </tr>

              <tr v-for="row in paymentConceptRows" :key="row.key">
                <td>
                  {{ row.dateLabel }}
                </td>
                <td>
                  <span>{{ row.folioLabel }}</span>
                </td>
                <td>
                  <div class="expenses-cell-stack">
                    <strong class="expenses-cell-main">{{ row.expenseClassLabel || '-' }}</strong>
                    <span class="expenses-cell-sub">{{ row.categoryLabel || '-' }}</span>
                  </div>
                </td>
                <td>
                  <div v-if="row.customerLabel || row.businessLabel" class="expenses-cell-stack">
                    <router-link
                      v-if="row.customerRoute"
                      :to="row.customerRoute"
                      class="expenses-table-link expenses-cell-main"
                    >
                      {{ row.customerLabel }}
                    </router-link>
                    <span v-else class="expenses-cell-main">{{ row.customerLabel }}</span>
                    <router-link
                      v-if="row.businessRoute"
                      :to="row.businessRoute"
                      class="expenses-table-link expenses-cell-sub"
                    >
                      {{ row.businessLabel }}
                    </router-link>
                    <span v-else class="expenses-cell-sub">{{ row.businessLabel }}</span>
                  </div>
                  <span v-else></span>
                </td>
                <td>{{ row.concept }}</td>
                <td class="app-text-right"><strong>{{ row.amountLabel }}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </article>

    <div class="invoice-create-bottom-grid">
      <RecentActivityCard
        :entity-id="expenseReceiptId"
        entity-type="expenseReceipt"
        :per-page="4"
        :show-footer="false"
      />

      <article class="invoice-create-card invoice-create-summary-card">
        <div class="invoice-create-summary-total">
          <span>{{ text.detail.cards.operation.fields.total }}</span>
          <div class="invoice-create-summary-value">
            <strong>{{ totalLabel }}</strong>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import RecentActivityCard from '@/components/common/RecentActivityCard.vue';
import { formatDateShort, formatDateTime, parsePresentationDate } from '@/utils/datePresentation';
import { formatAmountWithCurrency } from '@/utils/numberFormat';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: expenseReceiptsText } = useDomainText('expenseReceipts');

const BANK_DETAILS_ENABLED_PAYMENT_FORMS = new Set([
  '02',
  '03',
  '04',
  '05',
  '06',
  '28',
  '29',
  '99',
]);

export default {
  name: 'ExpenseReceiptDetailTabDetail',
  components: {
    RecentActivityCard,
  },
  props: {
    expenseReceipt: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      text: expenseReceiptsText,
      showFullSummary: false,
    };
  },
  computed: {
    fallback() {
      return '';
    },
    expenseReceiptId() {
      return Number(this.expenseReceipt?.id || 0) || 0;
    },
    normalizedCurrency() {
      return String(this.expenseReceipt?.currency || 'MXN').trim().toUpperCase() || 'MXN';
    },
    normalizedExchangeRate() {
      const numeric = Number(this.expenseReceipt?.exchange_rate);
      return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
    },
    paymentFormCode() {
      return String(this.expenseReceipt?.payment_form_code || this.expenseReceipt?.payment_form || '').trim().toUpperCase();
    },
    isCfdiMode() {
      const relationType = String(this.expenseReceipt?.relation_type || this.expenseReceipt?.voucher_type || '').trim().toLowerCase();
      return relationType !== 'simple_note' && Number(this.expenseReceipt?.special || 0) !== 1;
    },
    isStampedPayment() {
      const stampedFlag = Number(this.expenseReceipt?.is_stamped || 0) || 0;
      if (stampedFlag > 0) {
        return true;
      }

      const cfdiId = Number(this.expenseReceipt?.cfdi_id || 0) || 0;
      const cfdiPagoId = Number(this.expenseReceipt?.cfdi_pago_id || 0) || 0;
      return cfdiId > 0 && cfdiPagoId > 0;
    },
    showBankFields() {
      if (!this.isCfdiMode) {
        return false;
      }

      if (this.paymentFormCode === '') {
        return false;
      }

      return BANK_DETAILS_ENABLED_PAYMENT_FORMS.has(this.paymentFormCode);
    },
    canToggleSummary() {
      return this.isCfdiMode || this.issuerRfc !== '' || this.issuerBusinessName !== '' || this.issuerTaxRegime !== '' || this.issuerFiscalResidence !== '';
    },
    summaryTitle() {
      if (this.isCfdiMode) {
        return String(this.text?.detail?.cards?.fiscalSummary?.title || 'Resumen fiscal').trim();
      }

      return String(this.text?.detail?.cards?.summary?.title || 'Resumen').trim();
    },
    summaryToggleMoreLabel() {
      return String(this.text?.detail?.cards?.fiscalSummary?.actions?.showAll || 'Ver todo').trim();
    },
    summaryToggleLessLabel() {
      return String(this.text?.detail?.cards?.fiscalSummary?.actions?.showLess || 'Ver menos').trim();
    },
    showCurrencyExchange() {
      return this.normalizedCurrency !== 'MXN' && this.normalizedExchangeRate !== null;
    },
    statusLabel() {
      return String(this.expenseReceipt?.status || '').trim() || this.fallback;
    },
    statusClass() {
      const status = String(this.expenseReceipt?.status_key || '').trim().toLowerCase();
      return {
        'payment-receipts-status-pill--active': status !== 'canceled',
        'payment-receipts-status-pill--canceled': status === 'canceled',
      };
    },
    supplierRoute() {
      const supplierId = Number(this.expenseReceipt?.supplier_id || 0) || 0;
      if (supplierId <= 0) {
        return null;
      }

      return {
        name: 'supplierDetail',
        params: { supplierId: String(supplierId) },
      };
    },
    supplierName() {
      return String(this.expenseReceipt?.supplier_name || '').trim() || this.fallback;
    },
    relationLines() {
      const rawLines = Array.isArray(this.expenseReceipt?.relation_lines) ? this.expenseReceipt.relation_lines : [];
      const normalized = rawLines
        .map((raw, index) => {
          const reference = String(raw?.reference || '').trim();
          if (reference === '') {
            return null;
          }

          return {
            key: `relation-${index}-${reference}`,
            reference,
            route: this.resolveRelationRoute(raw),
          };
        })
        .filter((item) => item !== null);

      if (normalized.length > 0) {
        return normalized;
      }

      const fallbackReference = String(this.expenseReceipt?.relation || '').trim();
      if (fallbackReference === '') {
        return [];
      }

      return [{
        key: 'relation-fallback',
        reference: fallbackReference,
        route: null,
      }];
    },
    businessLines() {
      const backendRelatedBusinesses = Array.isArray(this.expenseReceipt?.related_businesses)
        ? this.expenseReceipt.related_businesses
        : [];
      const normalizedBackendRelated = backendRelatedBusinesses
        .map((raw, index) => {
          const label = String(raw?.label || '').trim();
          if (label === '' || this.isGenericBusinessLabel(label, raw)) {
            return null;
          }

          const saleId = Number(raw?.sale_id || 0) || 0;
          return {
            key: `related-business-${saleId || index}-${label}`,
            label,
            businessRoute: saleId > 0
              ? {
                name: 'saleDetail',
                params: { saleId: String(saleId) },
              }
              : null,
          };
        })
        .filter((item) => item !== null);

      if (normalizedBackendRelated.length > 0) {
        return normalizedBackendRelated;
      }

      const rawLines = Array.isArray(this.expenseReceipt?.relation_lines) ? this.expenseReceipt.relation_lines : [];
      const seen = new Set();
      const normalized = rawLines
        .map((raw) => {
          const businessName = String(raw?.business_name || raw?.sale_code || raw?.sale_name || '').trim();
          if (businessName === '' || this.isGenericBusinessLabel(businessName, raw)) {
            return null;
          }

          const saleId = Number(raw?.sale_id || 0) || 0;
          const dedupKey = saleId > 0 ? `sale-${saleId}` : `name-${businessName.toLowerCase()}`;
          if (seen.has(dedupKey)) {
            return null;
          }

          seen.add(dedupKey);

          return {
            key: dedupKey,
            label: businessName,
            businessRoute: this.resolveBusinessRoute(raw),
          };
        })
        .filter((item) => item !== null);

      if (normalized.length > 0) {
        return normalized;
      }

      const saleCode = String(this.expenseReceipt?.sale_code || '').trim();
      const saleName = String(this.expenseReceipt?.sale_name || '').trim();
      const fallbackBusiness = this.buildSaleLabel({ sale_code: saleCode, sale_name: saleName });
      if (fallbackBusiness === '' || this.isGenericBusinessLabel(fallbackBusiness, this.expenseReceipt || {})) {
        return [];
      }

      return [{
        key: 'business-fallback',
        label: fallbackBusiness,
        businessRoute: this.resolveBusinessRoute(this.expenseReceipt || {}),
      }];
    },
    paymentForm() {
      return String(this.expenseReceipt?.payment_form || '').trim() || this.fallback;
    },
    hasPaymentForm() {
      return this.paymentForm !== '';
    },
    paymentAccount() {
      return String(this.expenseReceipt?.payment_account || '').trim() || this.fallback;
    },
    operationNumber() {
      return String(this.expenseReceipt?.operation_number || '').trim() || this.fallback;
    },
    sentLabel() {
      if (typeof this.expenseReceipt?.sent !== 'boolean') {
        return '-';
      }

      return this.expenseReceipt?.sent ? 'Si' : 'No';
    },
    hasSentLabel() {
      return this.sentLabel !== '';
    },
    lastSentLabel() {
      return formatDateTime(this.expenseReceipt?.last_sent_at || this.expenseReceipt?.sent_at, { fallback: '' });
    },
    hasLastSentLabel() {
      return this.lastSentLabel !== '';
    },
    totalLabel() {
      return formatAmountWithCurrency(this.expenseReceipt?.total || 0, this.expenseReceipt?.currency || 'MXN');
    },
    folio() {
      return String(this.expenseReceipt?.folio || '').trim() || this.fallback;
    },
    showFolio() {
      return this.folio !== '';
    },
    cfdiFolio() {
      const serial = String(this.expenseReceipt?.payment_cfdi_serial || '').trim();
      const folio = String(this.expenseReceipt?.payment_cfdi_folio || '').trim();
      return `${serial}${folio}`.trim() || this.fallback;
    },
    showCfdiFolio() {
      return this.isStampedPayment && this.cfdiFolio !== '';
    },
    cfdiUuid() {
      return String(this.expenseReceipt?.payment_cfdi_uuid || '').trim() || this.fallback;
    },
    showCfdiUuid() {
      return this.isStampedPayment && this.cfdiUuid !== '';
    },
    emissionDateLabel() {
      const parsed = parsePresentationDate(this.expenseReceipt?.payment_cfdi_date || this.expenseReceipt?.date);
      if (!parsed) {
        return '';
      }

      const day = new Intl.DateTimeFormat('es-MX', { day: '2-digit' }).format(parsed);
      const month = new Intl.DateTimeFormat('es-MX', { month: 'long' }).format(parsed);
      const year = new Intl.DateTimeFormat('es-MX', { year: 'numeric' }).format(parsed);
      return `${day} de ${month} de ${year}`;
    },
    showEmissionDate() {
      return this.emissionDateLabel !== '';
    },
    currencyExchange() {
      if (!this.showCurrencyExchange) {
        return '';
      }

      return `${this.normalizedCurrency} / ${this.normalizedExchangeRate}`;
    },
    beneficiaryAccount() {
      const beneficiary = String(this.expenseReceipt?.bank_account_number || this.expenseReceipt?.cta_beneficiario || '').trim();
      const ordering = String(this.expenseReceipt?.cta_ordenante || '').trim();
      return beneficiary || ordering || this.fallback;
    },
    beneficiaryBankRfc() {
      return String(this.expenseReceipt?.rfc_emisor_cta_ben || this.expenseReceipt?.rfc_banco_beneficiario || '').trim() || this.fallback;
    },
    orderingBankName() {
      return String(this.expenseReceipt?.nom_banco_ord_ext || this.expenseReceipt?.bank_name || '').trim() || this.fallback;
    },
    orderingBankRfc() {
      return String(this.expenseReceipt?.rfc_emisor_cta_ord || '').trim() || this.fallback;
    },
    orderingBankAccount() {
      return String(this.expenseReceipt?.cta_ordenante || '').trim() || this.fallback;
    },
    issuerRfc() {
      const emissionMeta = this.expenseReceipt?.emission_trace?.meta;
      return String(
        this.expenseReceipt?.issuer_rfc
        || this.expenseReceipt?.emitter_rfc
        || emissionMeta?.emitter?.rfc
        || ''
      ).trim() || this.fallback;
    },
    issuerBusinessName() {
      const emissionMeta = this.expenseReceipt?.emission_trace?.meta;
      return String(
        this.expenseReceipt?.issuer?.business_name
        || this.expenseReceipt?.issuer?.name
        || this.expenseReceipt?.issuer_name
        || this.expenseReceipt?.emitter_name
        || emissionMeta?.emitter?.name
        || ''
      ).trim() || this.fallback;
    },
    issuerTaxRegime() {
      return String(
        this.expenseReceipt?.issuer?.tax_regime_label
        || this.expenseReceipt?.issuer?.tax_regime
        || this.expenseReceipt?.issuer?.regimen
        || ''
      ).trim() || this.fallback;
    },
    issuerFiscalResidence() {
      return String(
        this.expenseReceipt?.issuer?.fiscal_residence
        || this.expenseReceipt?.issuer?.zipcode
        || ''
      ).trim() || this.fallback;
    },
    receiverRfc() {
      return String(this.expenseReceipt?.receiver?.rfc || this.expenseReceipt?.receiver_rfc || '').trim() || this.fallback;
    },
    receiverBusinessName() {
      return String(
        this.expenseReceipt?.receiver?.business_name
        || this.expenseReceipt?.receiver_business_name
        || this.expenseReceipt?.supplier_name
        || ''
      ).trim() || this.fallback;
    },
    showReceiverRfc() {
      return this.isStampedPayment && this.receiverRfc !== '';
    },
    showReceiverBusinessName() {
      return this.isStampedPayment && this.receiverBusinessName !== '';
    },
    showBankDetailsOnExpanded() {
      return this.isStampedPayment && this.showBankFields;
    },
    paymentConceptRows() {
      const lines = Array.isArray(this.expenseReceipt?.relation_lines) ? this.expenseReceipt.relation_lines : [];

      return lines
        .map((line, index) => {
          const amount = Number(line?.amount || 0);
          if (!Number.isFinite(amount) || amount <= 0) {
            return null;
          }

          const backendBusinessLabel = String(line?.business_name || '').trim();
          const saleLabel = backendBusinessLabel || this.buildSaleLabel(line) || this.fallback;
          const isGenericBusiness = this.isGenericBusinessLabel(saleLabel, line);
          const reference = String(line?.reference || '').trim();
          const concept = String(line?.concept || '').trim() || this.fallback;
          const currency = String(line?.currency || this.normalizedCurrency).trim().toUpperCase() || this.normalizedCurrency;
          const dateLabel = formatDateShort(line?.date || '', { fallback: '-' });
          const folioLabel = String(line?.folio || reference || '').trim() || this.fallback;
          const customerLabel = String(line?.customer_name || '').trim();
          const customerId = Number(line?.customer_id || 0) || 0;
          const expenseClassLabel = String(line?.expense_class_name || line?.expenseClass || '').trim();
          const categoryLabel = String(line?.expense_category_name || line?.category || '').trim();
          const businessLabel = isGenericBusiness ? '' : saleLabel;

          return {
            key: `concept-${index}-${reference || saleLabel || amount}`,
            dateLabel,
            folioLabel,
            customerLabel,
            customerRoute: customerId > 0
              ? {
                name: 'customerDetail',
                params: { customerId: String(customerId) },
              }
              : null,
            businessLabel,
            businessRoute: businessLabel === '' ? null : this.resolveBusinessRoute(line),
            expenseClassLabel,
            categoryLabel,
            concept,
            amountLabel: formatAmountWithCurrency(amount, currency),
          };
        })
        .filter((item) => item !== null);
    },
  },
  methods: {
    buildSaleLabel(source) {
      const code = String(source?.sale_code || '').trim();
      const name = String(source?.sale_name || '').trim();

      if (code !== '' && name !== '') {
        return `${code} - ${name}`;
      }

      if (code !== '') {
        return code;
      }

      return name;
    },
    isGenericBusinessLabel(label, source = {}) {
      const normalizedLabel = String(label || '').trim().toLowerCase();
      const normalizedCode = String(source?.sale_code || '').trim().toLowerCase();
      const normalizedName = String(source?.sale_name || '').trim().toLowerCase();

      return normalizedLabel === 'sin negocio relacionado'
        || normalizedCode === 'generic'
        || normalizedCode === 'reference generic'
        || normalizedName === 'generic'
        || normalizedName === 'reference generic';
    },
    resolveSaleRoute(source) {
      const saleId = Number(source?.sale_id || 0) || 0;
      if (saleId <= 0) {
        return null;
      }

      return {
        name: 'saleDetail',
        params: { saleId: String(saleId) },
      };
    },
    resolveBusinessRoute(source) {
      const directRoute = this.resolveSaleRoute(source);
      if (directRoute) {
        return directRoute;
      }

      return this.resolveSaleRoute(this.expenseReceipt || {});
    },
    resolveRelationRoute(source) {
      const invoiceId = Number(source?.invoice_id || 0) || 0;
      if (invoiceId > 0) {
        return {
          name: 'invoiceDetail',
          params: { invoiceId: String(invoiceId) },
        };
      }

      return this.resolveSaleRoute(source);
    },
  },
};
</script>

<style scoped>
.payment-receipt-detail-top-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.payment-receipt-detail-card {
  height: 100%;
}

.payment-receipt-detail-operation-split {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.9fr);
  gap: 16px;
}

.payment-receipt-detail-operation-pane {
  min-width: 0;
}

.payment-receipt-detail-operation-pane--totals {
  border-left: 1px solid rgba(148, 163, 184, 0.32);
  padding-left: 14px;
}

.payment-receipt-detail-list {
  margin: 0;
  padding: 0;
}

.payment-receipt-detail-row {
  display: grid;
  grid-template-columns: minmax(130px, auto) minmax(0, 1fr);
  gap: 10px;
  margin-bottom: 8px;
}

.payment-receipt-detail-row--dual {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.payment-receipt-detail-row--dual > div dt {
  display: inline;
  margin: 0;
  font-size: 11px;
  color: #7b879a;
  font-weight: 600;
}

.payment-receipt-detail-row--dual > div dd {
  display: inline;
  margin: 0;
  margin-left: 8px;
  font-size: 12px;
  color: #334155;
  font-weight: 600;
  word-break: break-word;
}

.payment-receipt-detail-row--separator {
  border-bottom: 1px solid rgba(148, 163, 184, 0.32);
  padding-bottom: 8px;
  margin-bottom: 10px;
}

.payment-receipt-detail-row--multiline dd {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.payment-receipt-detail-row--total {
  border-top: 1px solid rgba(148, 163, 184, 0.32);
  padding-top: 10px;
  margin-top: 14px;
}

.payment-receipt-detail-row--total dd {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.payment-receipt-detail-row dt {
  margin: 0;
  font-size: 11px;
  color: #7b879a;
  font-weight: 600;
}

.payment-receipt-detail-row dd {
  margin: 0;
  font-size: 12px;
  color: #334155;
  font-weight: 600;
  word-break: break-word;
}

.payment-receipt-detail-multiline-item {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.payment-receipt-detail-link {
  color: #0070cd;
  font-weight: 600;
  text-decoration: none;
}

.payment-receipt-detail-link:hover {
  color: #005a9f;
  text-decoration: underline;
}

.payment-receipts-status-pill--active {
  color: #1c9b43;
  background: rgba(59, 212, 46, 0.14);
  border: 1px solid rgba(59, 212, 46, 0.3);
}

.payment-receipts-status-pill--canceled {
  color: #b42318;
  background: #fff0f0;
  border: 1px solid rgba(197, 58, 58, 0.3);
}

.payment-receipt-detail-summary-toggle {
  border: 0;
  background: transparent;
  padding: 0;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #0070cd;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.payment-receipt-detail-summary-toggle:hover {
  color: #005a9f;
}

.payment-receipt-detail-summary-expanded {
  border-top: 1px solid rgba(148, 163, 184, 0.34);
  margin-top: 12px;
  padding-top: 12px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.payment-receipt-detail-summary-block h4 {
  margin: 0 0 8px;
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.payment-receipt-detail-summary-payment-block {
  border-top: 1px solid rgba(148, 163, 184, 0.34);
  margin-top: 12px;
  padding-top: 12px;
}

.payment-receipt-detail-summary-split {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.9fr);
  gap: 14px;
}

.payment-receipt-detail-summary-pane {
  min-width: 0;
}

.payment-receipt-detail-summary-pane--operation {
  border-left: 1px solid rgba(148, 163, 184, 0.32);
  padding-left: 14px;
}

@media (max-width: 1100px) {
  .payment-receipt-detail-top-grid {
    grid-template-columns: 1fr;
  }

  .payment-receipt-detail-operation-split {
    grid-template-columns: 1fr;
  }

  .payment-receipt-detail-operation-pane--totals {
    border-left: 0;
    border-top: 1px solid rgba(148, 163, 184, 0.32);
    padding-left: 0;
    padding-top: 10px;
  }

  .payment-receipt-detail-row--dual {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .payment-receipt-detail-summary-expanded {
    grid-template-columns: 1fr;
  }

  .payment-receipt-detail-summary-split {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .payment-receipt-detail-summary-pane--operation {
    border-left: 0;
    border-top: 1px solid rgba(148, 163, 184, 0.32);
    padding-left: 0;
    padding-top: 10px;
  }
}
</style>
